import React, { Component } from 'react';

import { getTables, getSchedule, getDemoGames, scheduleForDate } from '../utils/reservationsApi';
import { CHARGE_MODE_LABEL } from '../utils/openTablesApi';

// Form de mesa abierta (FR-001…FR-008). Molde: FormEventComponent — alta y edición
// comparten campos; en edición se precargan desde props.initial (shape admin del backend)
// y sólo son editables cupo, precio, mínimo, juego y descripción (lo que acepta el PUT).
// Emite el payload por props.onChangeData(value); el backend re-valida todo.
class FormOpenTableComponent extends Component {
    constructor(props) {
        super(props);
        const initial = props.initial || {};
        this.state = {
            tableId: initial.tableId != null ? String(initial.tableId) : '',
            date: initial.date || '',
            startHour: initial.startHour != null ? String(initial.startHour) : '',
            cupo: initial.cupo != null ? String(initial.cupo) : '',
            price: initial.price != null ? String(initial.price) : '',
            chargeMode: initial.chargeMode || 'libre',
            minSuggested: initial.minSuggested != null ? String(initial.minSuggested) : '',
            gameCode: initial.gameCode || '',
            description: initial.description || '',
            tables: [],
            demoGames: [],
            schedule: null,
        };
    }

    componentDidMount() {
        this.loadTables();
        this.loadDemoGames();
        this.loadSchedule();
    }

    loadTables = async () => {
        try {
            // Sólo mesas activas: lo que se ofrece para sentar. En edición la mesa no se
            // cambia, pero la lista igual sirve para validar el cupo contra la capacidad.
            const data = await getTables();
            this.setState({ tables: (data && data.tables) || [] });
        } catch (error) {
            console.error('No se pudieron cargar las mesas:', error);
            alert(error.message || 'Error de conexión');
        }
    }

    loadDemoGames = async () => {
        try {
            // D-09: el juego anunciado sale del catálogo de la ludoteca (jugable hoy).
            const data = await getDemoGames();
            this.setState({ demoGames: (data && data.games) || [] });
        } catch (error) {
            console.error('No se pudieron cargar los juegos demo:', error);
        }
    }

    loadSchedule = async () => {
        try {
            const data = await getSchedule();
            const schedule = (data && data.schedule) || null;
            this.setState((prev) => ({
                schedule,
                // FR-008: el precio por lugar propone el cover vigente. Sólo en alta y sólo
                // si el campo sigue vacío (no pisar lo que el operador ya tipeó).
                price: (!this.props.editMode && String(prev.price).trim() === '' && schedule && schedule.pricePerPlayer != null)
                    ? String(schedule.pricePerPlayer)
                    : prev.price,
            }));
        } catch (error) {
            console.error('No se pudo cargar el horario del salón:', error);
        }
    }

    handleChange = (e) => {
        const { name, value } = e.target;
        this.setState({ [name]: value });
    }

    // Elegir la mesa propone su capacidad como cupo (pedido del dueño): lo normal es
    // publicar la mesa entera, y tipear el mismo número que dice el ayudín de abajo era
    // trabajo al pedo. Sigue siendo editable ("por si queremos meter más o menos gente")
    // y el tope no se mueve: validate() y el backend siguen rechazando cupo > capacidad.
    //
    // Es una RE-inicialización, no un default de una sola vez: cambiar de mesa vuelve a
    // poner la capacidad de la nueva, aunque el operador ya hubiera tipeado. Es lo
    // predecible (el cupo sigue a la mesa) y evita el caso peligroso al revés: quedarse
    // con el 8 de la mesa anterior sobre una mesa de 4 y comerse el rechazo al publicar.
    //
    // Sólo en ALTA. En edición el select no se renderiza (la mesa no se cambia después de
    // publicar), pero el guard va igual acá y no en el render: si algún día se permitiera
    // cambiarla, el cupo guardado —que puede ser menor a propósito y ya tiene gente
    // anotada— no se pisa solo.
    handleTableChange = (e) => {
        const value = e.target.value;
        if (this.props.editMode) {
            this.setState({ tableId: value });
            return;
        }
        const table = this.state.tables.find((t) => t.id === Number(value)) || null;
        this.setState({
            tableId: value,
            // Sin mesa (volver a "Seleccionar…") no hay capacidad que proponer: el cupo
            // queda vacío, como al abrir el form.
            cupo:    table && table.capacity != null ? String(table.capacity) : '',
        });
    }

    selectedTable = () => {
        const id = Number(this.state.tableId);
        if (!id) return null;
        return this.state.tables.find((t) => t.id === id) || null;
    }

    // Rango horario de la jornada de la fecha elegida (con override por día de semana).
    // Es una pista para el operador: la autoridad es el backend (FR-005).
    hourHint = () => {
        const { schedule, date } = this.state;
        if (!schedule || !date) return null;
        const day = scheduleForDate(schedule, date);
        if (day.openHour == null || day.lastReservation == null) return null;
        return `${day.openHour} a ${day.lastReservation}`;
    }

    validate = () => {
        const editMode = !!this.props.editMode;
        if (!editMode) {
            if (String(this.state.tableId).trim() === '') return 'Elegí la mesa física.';
            if (String(this.state.date).trim() === '') return 'Elegí la fecha.';
            const hour = Number(this.state.startHour);
            if (String(this.state.startHour).trim() === '' || !Number.isInteger(hour)) return 'Poné la hora de inicio (entera).';
        }
        const cupo = Number(this.state.cupo);
        if (String(this.state.cupo).trim() === '' || !Number.isInteger(cupo) || cupo < 1) {
            return 'El cupo tiene que ser un entero mayor a cero.';
        }
        // FR-002: el cupo no supera la capacidad de la mesa elegida. Validación de cortesía:
        // el backend la repite con la mesa lockeada.
        const table = this.selectedTable();
        if (table && cupo > Number(table.capacity)) {
            return `El cupo (${cupo}) supera la capacidad de la mesa ${table.label} (${table.capacity}).`;
        }
        if (String(this.state.price).trim() !== '' && !(Number(this.state.price) >= 0)) {
            return 'Precio por lugar inválido.';
        }
        if (String(this.state.minSuggested).trim() !== '') {
            const min = Number(this.state.minSuggested);
            if (!Number.isInteger(min) || min < 0) return 'El mínimo sugerido tiene que ser un entero mayor o igual a cero.';
        }
        return null;
    }

    buildPayload = () => {
        const editMode = !!this.props.editMode;
        const payload = { cupo: parseInt(this.state.cupo, 10) };

        // Precio vacío: en alta se omite (el backend propone el cover, FR-008); en edición
        // también, porque mandarlo vacío lo dejaría en 0 y "vacío" acá quiere decir "no tocar".
        if (String(this.state.price).trim() !== '') payload.price = Number(this.state.price);

        // '' en edición limpia el campo (el backend mapea '' → null / quitar juego).
        payload.minSuggested = String(this.state.minSuggested).trim() !== '' ? parseInt(this.state.minSuggested, 10) : '';
        payload.gameCode = this.state.gameCode || '';
        payload.description = this.state.description.trim();

        if (!editMode) {
            payload.tableId = parseInt(this.state.tableId, 10);
            payload.date = this.state.date;
            payload.startHour = parseInt(this.state.startHour, 10);
            payload.chargeMode = this.state.chargeMode;
        }
        return payload;
    }

    submit = () => {
        const problema = this.validate();
        if (problema) {
            alert(problema);
            return;
        }
        this.props.onChangeData(this.buildPayload());
    }

    field(label, name, type = 'text', extra = {}) {
        return (
            <div className="form-group">
                <label>{label}</label>
                <input
                    type={type}
                    name={name}
                    className="form-control"
                    value={this.state[name]}
                    onChange={this.handleChange}
                    {...extra}
                />
            </div>
        );
    }

    render() {
        const editMode = !!this.props.editMode;
        const table = this.selectedTable();
        const hourHint = this.hourHint();
        return (
            <div className="card card-small mb-4">
                <div className="card-body p-4">
                    <h6 className="text-uppercase text-muted">Datos de la mesa abierta</h6>
                    <div className="row">
                        <div className="col-md-4">
                            <div className="form-group">
                                <label>Mesa *</label>
                                {editMode ? (
                                    // La mesa física no se cambia después de publicar (el PUT no lo acepta).
                                    <input type="text" className="form-control" value={(this.props.initial && this.props.initial.tableLabel) || '—'} disabled />
                                ) : (
                                    <select name="tableId" className="form-control" value={this.state.tableId} onChange={this.handleTableChange}>
                                        <option value="">Seleccionar…</option>
                                        {this.state.tables.map((t) => (
                                            <option key={t.id} value={t.id}>
                                                {t.label} · {t.capacity} personas{Number(t.is_vip) === 1 ? ' · VIP' : ''}
                                            </option>
                                        ))}
                                    </select>
                                )}
                            </div>
                        </div>
                        <div className="col-md-4">{this.field('Fecha *', 'date', 'date', editMode ? { disabled: true } : {})}</div>
                        <div className="col-md-4">
                            <div className="form-group">
                                <label>Hora de inicio *</label>
                                <input type="number" name="startHour" className="form-control" min="0" max="25" step="1"
                                    value={this.state.startHour} onChange={this.handleChange} disabled={editMode} />
                                {!editMode && hourHint && (
                                    <small className="text-muted">Horario de esa jornada: {hourHint}.</small>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-3">
                            <div className="form-group">
                                <label>Cupo *</label>
                                <input type="number" name="cupo" className="form-control" min="1" step="1"
                                    max={table ? table.capacity : undefined}
                                    value={this.state.cupo} onChange={this.handleChange} />
                                {table && (
                                    // Ahora el campo viene con la capacidad puesta, así que el ayudín
                                    // ya no está para "copiá este número": está para el tope.
                                    <small className="text-muted">Capacidad de {table.label}: {table.capacity} (es el máximo).</small>
                                )}
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="form-group">
                                <label>Precio por lugar</label>
                                <input type="number" name="price" className="form-control" min="0"
                                    value={this.state.price} onChange={this.handleChange} />
                                {!editMode && (
                                    <small className="text-muted">Propuesto: el cover vigente del salón.</small>
                                )}
                            </div>
                        </div>
                        <div className="col-md-3">{this.field('Mínimo sugerido', 'minSuggested', 'number', { min: 0, step: 1 })}</div>
                        <div className="col-md-3">
                            <div className="form-group">
                                <label>Juego (opcional)</label>
                                <select name="gameCode" className="form-control" value={this.state.gameCode} onChange={this.handleChange}>
                                    <option value="">Sin juego anunciado</option>
                                    {this.state.demoGames.map((g) => (
                                        <option key={g.code} value={g.code}>{g.name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    <div className="form-group">
                        <label className="d-block">Modo de cobro *</label>
                        {editMode ? (
                            // El modo no se cambia después de publicar: hay inscripciones que ya
                            // se anotaron (y pagaron, o no) bajo esa regla.
                            <span>{CHARGE_MODE_LABEL[this.state.chargeMode] || this.state.chargeMode}</span>
                        ) : (
                            <>
                                <div className="custom-control custom-radio custom-control-inline">
                                    <input type="radio" id="chargeModeLibre" name="chargeMode" value="libre"
                                        className="custom-control-input"
                                        checked={this.state.chargeMode === 'libre'} onChange={this.handleChange} />
                                    <label className="custom-control-label" htmlFor="chargeModeLibre">
                                        Libre (se anotan sin pagar; el cover se cobra al llegar)
                                    </label>
                                </div>
                                <div className="custom-control custom-radio custom-control-inline">
                                    <input type="radio" id="chargeModeOnline" name="chargeMode" value="online"
                                        className="custom-control-input"
                                        checked={this.state.chargeMode === 'online'} onChange={this.handleChange} />
                                    <label className="custom-control-label" htmlFor="chargeModeOnline">
                                        Pago online (el lugar se reserva al pagar por MercadoPago)
                                    </label>
                                </div>
                            </>
                        )}
                    </div>

                    <div className="form-group">
                        <label>Descripción</label>
                        <textarea name="description" className="form-control" rows="3" maxLength="512"
                            value={this.state.description} onChange={this.handleChange} />
                    </div>

                    <button type="button" className="btn btn-accent" disabled={this.props.disabled} onClick={this.submit}>
                        {editMode ? 'Guardar cambios' : 'Publicar mesa abierta'}
                    </button>
                </div>
            </div>
        );
    }
}

export default FormOpenTableComponent;
